import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FileFilterProvider } from '@/contexts/fileFilter.tsx'
import { PortfolioDriveViewProvider } from '@/contexts/portfolioDriveViewProvider.tsx'
import { useDriveViewContext } from '@/contexts/driveViewContext.tsx'
import DriveViewer from '@/components/DriveViewer.tsx'
import { StatScopeProvider, useTrackStat } from '@/contexts/statsScopeContext.tsx'

export function PortfolioProjectPage() {
  const { portfolioId, projectId } = useParams<{ portfolioId: string, projectId: string }>()

  const statScope = useMemo(
    () => ({ source: 'portfolio' as const, portfolio: portfolioId, project: projectId }),
    [portfolioId, projectId],
  )

  return (
    <StatScopeProvider scope={statScope}>
      <FileFilterProvider>
        <PortfolioDriveViewProvider>
          <PortfolioProjectAutoNavigate />
        </PortfolioDriveViewProvider>
      </FileFilterProvider>
    </StatScopeProvider>
  )
}

// Auto-navigate to first album when none is selected
function PortfolioProjectAutoNavigate() {
  const { albums } = useDriveViewContext()
  const { portfolioId, projectId, albumId } = useParams<{ portfolioId: string, projectId: string, albumId?: string }>()
  const navigate = useNavigate()
  const track = useTrackStat()

  useEffect(() => {
    if (albums.length > 0 && !albumId) {
      navigate(`/p/${portfolioId}/${projectId}/${albums[0].id}`, { replace: true })
    }
  }, [albums, albumId, portfolioId, projectId, navigate])

  // Opening a project inside a portfolio. The `viewPortfolio` endpoints count
  // nothing themselves (the portal uses them for its preview), so this is the
  // only place the view is reported.
  useEffect(() => {
    if (!portfolioId || !projectId) return
    track('portfolio_view')
  }, [portfolioId, projectId])

  return <DriveViewer />
}
